import { Injectable, inject } from '@angular/core';
import { StatisticsApiService } from '@core/services/statistics-api.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, Observable, withLatestFrom } from 'rxjs';

import {
  companyColorPreset,
  globalColorPreset,
  groupColorTokenPreset,
  GroupType,
} from '@pages/statistics-page/constants';
import { ColoredStatisticsGroup } from '@pages/statistics-page/interfaces';

@Injectable({
  providedIn: 'root',
})
export class GroupsService {
  private api = inject(StatisticsApiService);
  private translate = inject(TranslateService);

  hasLoaded: boolean;
  private statisticsGroups$ = new BehaviorSubject<ColoredStatisticsGroup[]>([]);

  getGroups(): Observable<ColoredStatisticsGroup[]> {
    if (this.hasLoaded) {
      return this.statisticsGroups$;
    } else {
      return this.api.fetchGroups().pipe(
        withLatestFrom(this.statisticsGroups$),
        map(([groups, prevGroups]) => {
          const coloredGroup = groups.map((group, index) => ({
            ...group,
            // provide random negative id for GLOBAL and COMPANY group types if there were no groups fetched before
            // if they were fetched, we have to keep the same randomly generated ids
            id:
              group.id ||
              prevGroups.find(prevGroup => prevGroup.type === group.type)?.id ||
              -Math.floor(Math.random() * 256),
            ...this.getCustomGroupData(index, groups.length, group.type),
            hidden: group.type === GroupType.Global ? true : false,
          }));

          this.statisticsGroups$.next(coloredGroup);
          this.hasLoaded = true;

          return coloredGroup;
        })
      );
    }
  }

  private getCustomGroupData(
    groupIndex: number,
    arrLength: number,
    groupType: GroupType
  ): {
    colorToken: string;
    description?: string;
    labelTranslationKey?: string;
  } {
    switch (groupType) {
      case GroupType.Company:
        return {
          colorToken: companyColorPreset,
          description: this.translate.instant(
            'Statistics.SelectedDepartments.Company'
          ),
          labelTranslationKey: 'Statistics.SelectedDepartments.Company',
        };
      case GroupType.Global:
        return {
          colorToken: globalColorPreset,
          description: this.translate.instant(
            'Statistics.SelectedDepartments.GlobalAverage'
          ),
          labelTranslationKey: 'Statistics.SelectedDepartments.GlobalAverage',
        };
      default: {
        const colorPresetLength = Object.values(groupColorTokenPreset).length;

        return {
          colorToken:
            arrLength <= colorPresetLength
              ? groupColorTokenPreset[groupIndex]
              : groupColorTokenPreset[
                  Math.floor(Math.random() * colorPresetLength)
                ],
        };
      }
    }
  }
}
